"use client"

import { useState, useEffect } from "react"
import {
  Package,
  ChevronLeft,
  ChevronRight,
  Building2,
  Menu,
  X,
  LogOut,
  User,
  Lock,
  Eye,
  EyeOff,
  Shield,
  Zap,
  TrendingUp,
  Scan,
  BarChart3,
  Logs,
  Bell,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Check,
  Clock,
  FileText,
  ShoppingCart,
  Truck,
  ChevronDown
} from "lucide-react"

// IntelligentIntegrationSystem components
import PointOfSale from "./IntelligentIntegrationSystem/PointOfSale"
import CentralSystem from "./IntelligentIntegrationSystem/CentralSystem"
import POSAnalytics from "./IntelligentIntegrationSystem/Analytics"

// WarehouseReception components
import RegisterProduct from "./WarehouseReception/RegisterProduct"
import ProductsList from "./WarehouseReception/ProductsList"
import TruckManagement from "./WarehouseReception/TrucksManagement"
import RoutesAndMaps from "./WarehouseReception/RoutesAndMaps"
import WarehouseManagement from "./WarehouseReception/WarehouseManagement"
import WarehouseAnalytics from "./WarehouseReception/Analytics"

import ReglaAutomatizacion from "./components/ReglaAutomatizacion"
import Configuracion from "./components/Configuracion"
import OrdenManagement from "./WarehouseReception/OrderManagement"
import { authFetch, setLogoutCallback } from "./utils/authFetch"

// URL de la API — runtime detection: si no hay env var Y no estamos en localhost, usar producción
const API_URL = process.env.REACT_APP_API_URL
  || (window.location.hostname === 'localhost'
    ? 'http://localhost:3001/api'
    : 'https://alvarezbohlback-production.up.railway.app/api');

const App = () => {
  // ============================================
  // ESTADOS DE AUTENTICACIÓN
  // ============================================
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [loginForm, setLoginForm] = useState({ email: "", password: "" })
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const [activeComponent, setActiveComponent] = useState("inventory")
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // ============================================
  // ESTADOS DE NOTIFICACIONES
  // ============================================
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const [notificationsLoading, setNotificationsLoading] = useState(false)

  // eslint-disable-next-line no-unused-vars
  const [products, setProducts] = useState([
    {
      productId: "P-001",
      category: "Electrónica",
      scanResult: "good",
      confidence: 95,
      timestamp: new Date(2025, 0, 13, 10, 30),
      supplier: "Proveedor A",
    },
    {
      productId: "P-002",
      category: "Alimentos",
      scanResult: "good",
      confidence: 88,
      timestamp: new Date(2025, 0, 13, 11, 15),
      supplier: "Proveedor B",
    },
    {
      productId: "P-003",
      category: "Ropa",
      scanResult: "defective",
      confidence: 65,
      timestamp: new Date(2025, 0, 13, 12, 45),
      supplier: "Proveedor A",
    },
    {
      productId: "P-004",
      category: "Electrónica",
      scanResult: "good",
      confidence: 92,
      timestamp: new Date(2025, 0, 13, 13, 20),
      supplier: "Proveedor C",
    },
    {
      productId: "P-005",
      category: "Hogar",
      scanResult: "good",
      confidence: 90,
      timestamp: new Date(2025, 0, 13, 14, 10),
      supplier: "Proveedor B",
    },
  ])

  // ============================================
  // MENÚ SEGÚN ROL (items planos, sin desplegable)
  // ============================================
  const getMenuItemsByRole = (rol) => {
    return [
      {
        id: "inventory",
        label: "Inventario",
        icon: Scan,
      },
      {
        id: "warehouse-management",
        label: "Gestión de almacén",
        icon: Package,
      },
      {
        id: "order-management",
        label: "Gestión de pedidos",
        icon: Logs,
        subItems: [
          {
            id: "order-management-compraventa",
            label: "Compra y Venta",
            icon: ShoppingCart,
            mode: "compra_venta",
          },
          {
            id: "order-management-placa",
            label: "Pedidos con Placa",
            icon: Truck,
            mode: "placa",
          },
        ],
      },

      // Solo admin ve estadísticas de almacén
      ...(rol === 'admin' ? [{
        id: "warehouse-analytics",
        label: "Estadísticas",
        icon: BarChart3,
      }] : []),
    ];
  };

  const [menuItems, setMenuItems] = useState([]);
  const [expandedMenuItems, setExpandedMenuItems] = useState({});

  // ============================================
  // EFECTOS - PERSISTENCIA DE SESIÓN
  // ============================================
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
        setMenuItems(getMenuItemsByRole(parsedUser.rol));
      } catch (error) {
        console.error('Error al parsear usuario guardado:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user && token) {
      fetchNotifications();
      fetchUnreadCount();

      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 60000);

      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, token]);

  // ============================================
  // FUNCIONES DE AUTENTICACIÓN
  // ============================================
  const handleLogin = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setLoginError("")

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al iniciar sesión');
      }

      const { access_token } = result.data.session;
      const userData = result.data.user;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      setMenuItems(getMenuItemsByRole(userData.rol));
      setLoginError("");
      setLoginForm({ email: "", password: "" });
      setActiveComponent("inventory");

    } catch (error) {
      console.error('Error en login:', error);
      setLoginError(error.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  }

  const forceLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setLoginForm({ email: "", password: "" });
    setLoginError("");
    setNotifications([]);
    setUnreadCount(0);
    setShowNotifications(false);
    setMenuItems([]);
    setActiveComponent("inventory");
  };

  useEffect(() => {
    setLogoutCallback(forceLogout);
  }, []);

  const handleLogout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      forceLogout();
    }
  }

  const handleLoginChange = (e) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value,
    })
    if (loginError) setLoginError("")
  }

  // ============================================
  // FUNCIONES DE NOTIFICACIONES (con token)
  // ============================================
  const fetchNotifications = async () => {
    if (!token) return;

    try {
      setNotificationsLoading(true);
      const response = await authFetch(`${API_URL}/notifications?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setNotifications(result.data);
      }
    } catch (error) {
      console.error('Error obteniendo notificaciones:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    if (!token) return;

    try {
      const response = await authFetch(`${API_URL}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        setUnreadCount(result.unread_count);
      }
    } catch (error) {
      console.error('Error obteniendo contador de no leídas:', error);
    }
  };

  const markAsRead = async (id) => {
    if (!token) return;

    try {
      const response = await authFetch(`${API_URL}/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        fetchNotifications();
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!token) return;

    try {
      const response = await authFetch(`${API_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        fetchNotifications();
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
    }
  };

  const deleteNotification = async (id) => {
    if (!token) return;

    try {
      const response = await authFetch(`${API_URL}/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        fetchNotifications();
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'vencido':
        return <AlertCircle size={20} color="#ef4444" />;
      case 'proximo_vencer':
        return <AlertTriangle size={20} color="#f59e0b" />;
      default:
        return <AlertCircle size={20} color="#3b82f6" />;
    }
  };

  const getTipoColors = (tipo) => {
    switch (tipo) {
      case 'vencido':
        return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
      case 'proximo_vencer':
        return { bg: '#fffbeb', border: '#fde68a', text: '#d97706' };
      default:
        return { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' };
    }
  };

  const formatTimeAgo = (fecha) => {
    const now = new Date();
    const notifDate = new Date(fecha);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return notifDate.toLocaleDateString('es-ES');
  };

  // ============================================
  // FUNCIÓN PARA ABRIR JIRA
  // ============================================
  const handleOpenJiraIncident = () => {
    window.open('https://buildingblocksteam.atlassian.net/jira/software/form/f6423eed-8cf4-4f3c-90dd-1d906d5a10ac', '_blank');
  };

  // ============================================
  // HANDLERS DE NAVEGACIÓN
  // ============================================
  const handleMenuItemClick = (itemId) => {
    setActiveComponent(itemId)
    setIsMobileMenuOpen(false)
    const parent = menuItems.find(m => Array.isArray(m.subItems) && m.subItems.some(s => s.id === itemId));
    if (parent) {
      setExpandedMenuItems(prev => ({ ...prev, [parent.id]: true }));
    }
  }

  const getActiveComponent = () => {
    const componentMap = {
      // Point of Sale components
      supermercado: PointOfSale,
      "central-system": CentralSystem,
      "pos-analytics": POSAnalytics,
      // Warehouse components
      inventory: RegisterProduct,
      products: ProductsList,
      vehicles: TruckManagement,
      routes: RoutesAndMaps,
      "warehouse-management": WarehouseManagement,
      "warehouse-analytics": WarehouseAnalytics,
      "order-management": OrdenManagement,
      "order-management-compraventa": OrdenManagement,
      "order-management-placa": OrdenManagement,
      // Other components
      automatizacion: ReglaAutomatizacion,
      configuracion: Configuracion,
    }

    return componentMap[activeComponent] || null
  }

  const getActiveComponentProps = () => {
    if (activeComponent === "order-management-placa") return { vistaPedidos: "placa" };
    if (activeComponent === "order-management-compraventa") return { vistaPedidos: "compra_venta" };
    return {};
  }

  const ActiveComponent = getActiveComponent()
  const activeComponentExtraProps = getActiveComponentProps()

  // ============================================
  // PANTALLA DE LOGIN
  // ============================================
  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>

        <div className="login-wrapper">
          <div className="login-brand-panel">
            <div className="brand-content">
              <div className="brand-header">
                <div className="brand-logo">
                  <Building2 size={48} />
                </div>
                <h1>Sistema Central</h1>
                <p>Plataforma Integral de Gestión</p>
              </div>

              <div className="features-list">
                <div className="feature-item">
                  <div className="feature-icon">
                    <Shield size={20} />
                  </div>
                  <div className="feature-text">
                    <h4>Seguridad Avanzada</h4>
                    <p>Protección de datos empresariales de nivel corporativo</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <Zap size={20} />
                  </div>
                  <div className="feature-text">
                    <h4>Automatización Inteligente</h4>
                    <p>Optimiza procesos y aumenta la productividad</p>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">
                    <TrendingUp size={20} />
                  </div>
                  <div className="feature-text">
                    <h4>Analytics en Tiempo Real</h4>
                    <p>Métricas y reportes para decisiones informadas</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="brand-footer">
              <p>© 2025 Sistema Central. Todos los derechos reservados.</p>
            </div>
          </div>

          <div className="login-form-panel">
            <div className="form-container">
              <div className="form-header">
                <h2>Bienvenido de nuevo</h2>
                <p>Ingresa tus credenciales para acceder a tu panel de control</p>
              </div>

              {loginError && (
                <div className="error-alert">
                  <div className="error-icon">⚠️</div>
                  <div className="error-text">
                    <strong>Error de autenticación</strong>
                    <p>{loginError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleLogin} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <div className="input-container">
                    <User className="input-icon" size={20} />
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={loginForm.email}
                      onChange={handleLoginChange}
                      placeholder="correo@ejemplo.com"
                      required
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Contraseña</label>
                  <div className="input-container">
                    <Lock className="input-icon" size={20} />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={loginForm.password}
                      onChange={handleLoginChange}
                      placeholder="Ingresa tu contraseña"
                      required
                      disabled={isLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input type="checkbox" />
                    <span className="checkmark"></span>
                    <span>Recordar sesión</span>
                  </label>
                </div>

                <button type="submit" className="login-button" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="spinner"></div>
                      <span>Iniciando sesión...</span>
                    </>
                  ) : (
                    <>
                      <span>Iniciar Sesión</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <style jsx>{`
          .login-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            position: relative;
            overflow: hidden;
          }

          .bg-shapes {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
            z-index: 0;
          }

          .login-wrapper {
            width: 100%;
            max-width: 1200px;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            box-shadow: 0 32px 64px rgba(0, 0, 0, 0.2);
            display: grid;
            grid-template-columns: 1fr 1fr;
            min-height: 700px;
            position: relative;
            z-index: 1;
            overflow: hidden;
          }

          .login-brand-panel {
            background: linear-gradient(135deg, #1a202c 0%, #2d3748 50%, #4a5568 100%);
            color: white;
            padding: 60px 50px 40px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            position: relative;
            overflow: hidden;
          }

          .login-brand-panel::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            opacity: 0.6;
          }

          .brand-content {
            position: relative;
            z-index: 2;
          }

          .brand-header {
            text-align: center;
            margin-bottom: 50px;
          }

          .brand-logo {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 24px;
            box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
          }

          .brand-header h1 {
            font-size: 2.5rem;
            font-weight: 800;
            margin: 0 0 12px 0;
            background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .brand-header p {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0;
            font-weight: 300;
            line-height: 1.5;
          }

          .features-list {
            margin: 0 0 40px 0;
          }

          .feature-item {
            display: flex;
            align-items: flex-start;
            gap: 16px;
            margin-bottom: 28px;
            padding: 20px;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 16px;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            transition: all 0.3s ease;
          }

          .feature-item:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: translateX(8px);
          }

          .feature-icon {
            width: 44px;
            height: 44px;
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2));
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .feature-text h4 {
            font-size: 1rem;
            font-weight: 600;
            margin: 0 0 6px 0;
            color: #f7fafc;
          }

          .feature-text p {
            font-size: 0.9rem;
            opacity: 0.8;
            margin: 0;
            line-height: 1.4;
          }

          .brand-footer {
            position: relative;
            z-index: 2;
            text-align: center;
            opacity: 0.7;
          }

          .brand-footer p {
            font-size: 0.8rem;
            margin: 0;
          }

          .login-form-panel {
            padding: 60px 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
          }

          .form-container {
            width: 100%;
            max-width: 400px;
          }

          .form-header {
            text-align: center;
            margin-bottom: 40px;
          }

          .form-header h2 {
            font-size: 2.25rem;
            font-weight: 700;
            color: #1a202c;
            margin: 0 0 12px 0;
            letter-spacing: -0.025em;
          }

          .form-header p {
            color: #64748b;
            font-size: 1rem;
            margin: 0;
            line-height: 1.5;
          }

          .error-alert {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            background: rgba(239, 68, 68, 0.05);
            border: 1px solid rgba(239, 68, 68, 0.2);
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 32px;
          }

          .error-icon {
            font-size: 1.2rem;
            flex-shrink: 0;
          }

          .error-text strong {
            display: block;
            color: #dc2626;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .error-text p {
            color: #7f1d1d;
            font-size: 0.9rem;
            margin: 0;
          }

          .login-form {
            margin-bottom: 32px;
          }

          .form-group {
            margin-bottom: 24px;
          }

          .form-group label {
            display: block;
            font-weight: 600;
            color: #374151;
            margin-bottom: 8px;
            font-size: 0.95rem;
          }

          .input-container {
            position: relative;
          }

          .input-container input {
            width: 79%;
            background: #f8fafc;
            border: 2px solid #e2e8f0;
            border-radius: 12px;
            padding: 16px 20px 16px 56px;
            font-size: 1rem;
            transition: all 0.3s ease;
            color: #1a202c;
          }

          .input-container input:focus {
            background: #ffffff;
            border-color: #667eea;
            box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
            outline: none;
          }

          .input-container input:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .input-icon {
            position: absolute;
            left: 18px;
            top: 50%;
            transform: translateY(-50%);
            color: #64748b;
            z-index: 2;
          }

          .password-toggle {
            position: absolute;
            right: 18px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #64748b;
            cursor: pointer;
            padding: 4px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .password-toggle:hover {
            color: #667eea;
            background: rgba(102, 126, 234, 0.1);
          }

          .form-options {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 32px;
            flex-wrap: wrap;
            gap: 16px;
          }

          .checkbox-label {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 0.9rem;
            color: #4b5563;
            cursor: pointer;
            user-select: none;
          }

          .checkbox-label input[type="checkbox"] {
            display: none;
          }

          .checkmark {
            width: 18px;
            height: 18px;
            border: 2px solid #d1d5db;
            border-radius: 4px;
            position: relative;
            transition: all 0.2s ease;
          }

          .checkbox-label input[type="checkbox"]:checked + .checkmark {
            background: #667eea;
            border-color: #667eea;
          }

          .checkbox-label input[type="checkbox"]:checked + .checkmark::after {
            content: '';
            position: absolute;
            top: 2px;
            left: 5px;
            width: 4px;
            height: 8px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }

          .login-button {
            width: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 12px;
            padding: 18px 24px;
            font-size: 1.05rem;
            font-weight: 600;
            color: white;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            position: relative;
            overflow: hidden;
          }

          .login-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: left 0.5s ease;
          }

          .login-button:hover::before {
            left: 100%;
          }

          .login-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
          }

          .login-button:disabled {
            opacity: 0.7;
            transform: none;
            cursor: not-allowed;
          }

          .spinner {
            width: 20px;
            height: 20px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top: 2px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @media (max-width: 1024px) {
            .login-wrapper {
              grid-template-columns: 1fr;
              max-width: 500px;
            }

            .login-brand-panel {
              display: none;
            }

            .login-form-panel {
              padding: 40px 30px;
            }
          }

          @media (max-width: 768px) {
            .login-container {
              padding: 16px;
            }

            .login-wrapper {
              border-radius: 16px;
              min-height: auto;
            }

            .login-form-panel {
              padding: 32px 24px;
            }

            .form-header h2 {
              font-size: 1.8rem;
            }

            .form-options {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }
          }

          @media (max-width: 480px) {
            .form-container {
              max-width: none;
            }

            .login-form-panel {
              padding: 24px 16px;
            }

            .input-container input {
              padding: 14px 16px 14px 48px;
            }

            .login-button {
              padding: 16px 20px;
            }
          }
        `}</style>
      </div>
    )
  }

  // ============================================
  // PANTALLA PRINCIPAL CON NOTIFICACIONES
  // ============================================

  return (
    <div className="main-app">
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} />}

      {/* SIDEBAR */}
      <div className={`main-sidebar ${isMenuCollapsed ? "collapsed" : ""} ${isMobileMenuOpen ? "mobile-open" : ""}`}>
        <div className="main-sidebar-header">
          <div className="main-logo">
            <div className="main-logo-icon">
              <Building2 className="icon" />
            </div>
            {!isMenuCollapsed && (
              <div className="main-logo-text">
                <h1>Sistema Central</h1>
                <p>{user?.nombre} - {user?.rol === 'admin' ? 'Admin' : user?.rol === 'jefe_almacen' ? 'Jefe' : 'Operario'}</p>
              </div>
            )}
          </div>

          <div className="main-sidebar-controls">
            {/* BOTÓN DE REGISTRAR INCIDENCIA */}
            <button
              className="incident-button"
              onClick={handleOpenJiraIncident}
              title="Registrar nueva incidencia"
            >
              <FileText size={18} />
            </button>

            {/* ICONO DE NOTIFICACIONES */}
            <button
              className="notifications-icon-button"
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (!showNotifications) {
                  fetchNotifications();
                }
              }}
              title="Notificaciones"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="notification-badge-header">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            <button className="close-mobile-menu" onClick={() => setIsMobileMenuOpen(false)}>
              <X size={18} />
            </button>
          </div>

          {/* DROPDOWN DE NOTIFICACIONES */}
          {showNotifications && (
            <>
              <div 
                className="notifications-overlay"
                onClick={() => setShowNotifications(false)}
              />
              
              <div className="notifications-dropdown-sidebar">
                <div className="notifications-header">
                  <h3>
                    <Bell size={18} style={{ marginRight: '8px', display: 'inline' }} />
                    Notificaciones
                  </h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllAsRead} className="mark-all-read">
                      <Check size={14} />
                      Marcar todas
                    </button>
                  )}
                </div>

                <div className="notifications-list">
                  {notificationsLoading ? (
                    <div className="notifications-loading">
                      <div className="spinner-small"></div>
                      <p>Cargando notificaciones...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="no-notifications">
                      <CheckCircle size={40} color="#10b981" />
                      <p>No tienes notificaciones</p>
                      <span>Estás al día con todas las alertas</span>
                    </div>
                  ) : (
                    notifications.map((notif) => {
                      const colors = getTipoColors(notif.tipo);
                      return (
                        <div
                          key={notif.id}
                          className={`notification-item ${!notif.leida ? 'unread' : ''}`}
                          style={{
                            background: colors.bg,
                            borderLeft: `3px solid ${colors.border}`
                          }}
                        >
                          <div className="notification-icon">
                            {getTipoIcon(notif.tipo)}
                          </div>

                          <div className="notification-content">
                            <div className="notification-tipo">
                              {notif.tipo === 'vencido' ? 'PRODUCTO VENCIDO' : 'Próximo a Vencer'}
                            </div>
                            <div className="notification-producto">
                              {notif.producto_descripcion}
                            </div>
                            <div className="notification-details">
                              <span className="detail-item">
                                📦 Lote: {notif.lote_numero}
                              </span>
                              <span className="detail-item">
                                📍 {notif.ubicacion_codigo}
                              </span>
                              <span className="detail-item">
                                <Clock size={12} />
                                {formatTimeAgo(notif.created_at)}
                              </span>
                              {notif.dias_restantes !== null && (
                                <span className="detail-item" style={{ fontWeight: 600, color: colors.text }}>
                                  {notif.dias_restantes < 0 
                                    ? `Venció hace ${Math.abs(notif.dias_restantes)} días`
                                    : `Vence en ${notif.dias_restantes} días`
                                  }
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="notification-actions">
                            {!notif.leida && (
                              <button
                                onClick={() => markAsRead(notif.id)}
                                className="action-button"
                                title="Marcar como leída"
                              >
                                <Check size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notif.id)}
                              className="action-button delete"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="notifications-footer">
                    <button onClick={() => setShowNotifications(false)}>
                      Cerrar
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* NAV - items planos + items con subItems desplegables */}
        <nav className="main-nav">
          {menuItems.map((item) => {
            const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;
            const isExpanded = !!expandedMenuItems[item.id];
            const isParentActive = hasSubItems && item.subItems.some(sub => sub.id === activeComponent);

            if (!hasSubItems) {
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemClick(item.id)}
                  className={`main-nav-item ${activeComponent === item.id ? "active" : ""}`}
                >
                  <div className="main-nav-icon">
                    <item.icon size={20} />
                  </div>
                  {!isMenuCollapsed && (
                    <span className="main-nav-label">{item.label}</span>
                  )}
                  {activeComponent === item.id && <div className="main-nav-indicator" />}
                </button>
              );
            }

            return (
              <div key={item.id} className="main-nav-group">
                <button
                  onClick={() => {
                    if (isMenuCollapsed) {
                      handleMenuItemClick(item.subItems[0].id);
                    } else {
                      setExpandedMenuItems(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                    }
                  }}
                  className={`main-nav-item ${isParentActive ? "active-parent" : ""}`}
                >
                  <div className="main-nav-icon">
                    <item.icon size={20} />
                  </div>
                  {!isMenuCollapsed && (
                    <>
                      <span className="main-nav-label">{item.label}</span>
                      <ChevronDown
                        size={16}
                        style={{
                          marginLeft: 'auto',
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          opacity: 0.6,
                        }}
                      />
                    </>
                  )}
                </button>
                {!isMenuCollapsed && isExpanded && (
                  <div className="main-nav-subitems">
                    {item.subItems.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => handleMenuItemClick(sub.id)}
                        className={`main-nav-subitem ${activeComponent === sub.id ? "active" : ""}`}
                      >
                        <div className="main-nav-subicon">
                          <sub.icon size={16} />
                        </div>
                        <span className="main-nav-sublabel">{sub.label}</span>
                        {activeComponent === sub.id && <div className="main-nav-indicator" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="main-sidebar-footer">
          <button className="logout-button" onClick={handleLogout} title="Cerrar sesión">
            <LogOut size={18} />
            {!isMenuCollapsed && <span>Cerrar Sesión</span>}
          </button>

          <button
            className="toggle-main-sidebar-footer"
            onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
            title={isMenuCollapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {isMenuCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      </div>

      {/* BOTÓN DE MENÚ MÓVIL */}
      <button className="mobile-menu-button" onClick={() => setIsMobileMenuOpen(true)}>
        <Menu size={20} />
      </button>

      {/* CONTENIDO PRINCIPAL */}
      <div className={`main-content ${isMenuCollapsed ? "expanded" : ""}`}>
        {ActiveComponent && activeComponent === "warehouse-analytics" ? (
          <ActiveComponent products={products} token={token} />
        ) : (
          ActiveComponent && <ActiveComponent token={token} user={user} {...activeComponentExtraProps} />
        )}
      </div>

      <style jsx>{`
        .main-app {
          display: flex;
          min-height: 100vh;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
        }

        .main-sidebar {
          width: 280px;
          background: linear-gradient(180deg, #1a202c 0%, #2d3748 100%);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 1000;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
        }

        .main-sidebar.collapsed {
          width: 70px;
        }

        .main-sidebar.collapsed .main-nav-item {
          justify-content: center;
          padding: 12px 8px;
        }

        .main-sidebar.collapsed .main-nav-icon {
          margin: 0;
        }

        .main-sidebar.collapsed .main-nav-label {
          display: none;
        }

        .main-sidebar.collapsed .main-nav-indicator {
          display: none;
        }

        .main-sidebar.collapsed .toggle-main-sidebar-footer {
          width: 38px;
          height: 38px;
          margin: 0 auto;
        }

        .main-sidebar.collapsed .logout-button {
          width: 38px;
          height: 38px;
          margin: 0 auto 16px auto;
          justify-content: center;
        }

        .main-sidebar.collapsed .logout-button span {
          display: none;
        }

        .main-sidebar-header {
          padding: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 80px;
          position: relative;
        }

        .main-sidebar.collapsed .main-sidebar-header {
          justify-content: center;
          padding: 20px 10px;
          flex-direction: column;
          gap: 12px;
        }

        .main-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .main-sidebar.collapsed .main-logo {
          flex: 0;
        }

        .main-logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .main-logo-icon .icon {
          color: white;
          width: 22px;
          height: 22px;
        }

        .main-logo-text h1 {
          color: white;
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 2px 0;
        }

        .main-logo-text p {
          color: #a0aec0;
          font-size: 13px;
          margin: 0;
        }

        .main-sidebar-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .main-sidebar.collapsed .main-sidebar-controls {
          flex-direction: column;
          width: 100%;
        }

        .incident-button {
          position: relative;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          background: rgba(34, 197, 94, 0.15);
          color: #a0aec0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .incident-button:hover {
          background: rgba(34, 197, 94, 0.25);
          color: #22c55e;
          transform: scale(1.05);
        }

        .notifications-icon-button {
          position: relative;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          background: rgba(102, 126, 234, 0.15);
          color: #a0aec0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .notifications-icon-button:hover {
          background: rgba(102, 126, 234, 0.25);
          color: white;
          transform: scale(1.05);
        }

        .notification-badge-header {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 16px;
          height: 16px;
          padding: 0 4px;
          background: #ef4444;
          color: white;
          border-radius: 8px;
          font-size: 9px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #2d3748;
          animation: pulse-badge 2s infinite;
        }

        @keyframes pulse-badge {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }

        .close-mobile-menu {
          display: none;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          background: rgba(255, 255, 255, 0.1);
          color: #a0aec0;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .close-mobile-menu:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .main-nav {
          flex: 1;
          padding: 20px 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          overflow-y: auto;
        }

        .main-nav-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 13px 16px;
          border-radius: 12px;
          border: none;
          background: transparent;
          color: #a0aec0;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          text-align: left;
          width: 100%;
          min-height: 48px;
        }

        .main-nav-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
          opacity: 0;
          transition: opacity 0.3s ease;
          border-radius: 12px;
        }

        .main-nav-item:hover::before {
          opacity: 1;
        }

        .main-nav-item:hover {
          color: white;
          transform: translateX(4px);
        }

        .main-sidebar.collapsed .main-nav-item:hover {
          transform: scale(1.1);
        }

        .main-nav-item.active {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2));
          color: white;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.2);
        }

        .main-nav-item.active::before {
          opacity: 1;
        }

        .main-nav-icon {
          flex-shrink: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .main-nav-label {
          flex: 1;
          font-size: 14px;
          font-weight: 600;
          z-index: 1;
        }

        .main-nav-indicator {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 20px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 2px;
        }

        .main-nav-group {
          display: flex;
          flex-direction: column;
        }

        .main-nav-item.active-parent {
          color: white;
        }

        .main-nav-subitems {
          display: flex;
          flex-direction: column;
          margin: 4px 0 8px 16px;
          padding-left: 12px;
          border-left: 2px solid rgba(102, 126, 234, 0.25);
          gap: 2px;
        }

        .main-nav-subitem {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          position: relative;
          transition: all 0.2s ease;
          text-align: left;
        }

        .main-nav-subitem:hover {
          background: rgba(255, 255, 255, 0.06);
          color: white;
        }

        .main-nav-subitem.active {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.25), rgba(118, 75, 162, 0.25));
          color: white;
          font-weight: 600;
        }

        .main-nav-subicon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          opacity: 0.85;
        }

        .main-nav-sublabel {
          flex: 1;
        }

        .main-sidebar.collapsed .main-nav-subitems {
          display: none;
        }

        .main-sidebar-footer {
          padding: 20px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .logout-button {
          width: 100%;
          height: 40px;
          border-radius: 8px;
          border: none;
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s ease;
          font-size: 14px;
          font-weight: 500;
        }

        .logout-button:hover {
          background: rgba(220, 53, 69, 0.2);
          color: #dc3545;
          transform: translateY(-1px);
        }

        .toggle-main-sidebar-footer {
          width: 100%;
          height: 40px;
          border-radius: 8px;
          border: none;
          background: rgba(255, 255, 255, 0.1);
          color: #a0aec0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          font-size: 14px;
          font-weight: 500;
        }

        .toggle-main-sidebar-footer:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          transform: translateY(-1px);
        }

        /* ============================================
           NOTIFICACIONES - DROPDOWN
           ============================================ */
        
        .notifications-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1100;
        }

        .notifications-dropdown-sidebar {
          position: fixed;
          top: 90px;
          left: 296px;
          width: 420px;
          max-width: calc(100vw - 320px);
          max-height: calc(100vh - 120px);
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
          z-index: 1200;
          border: 1px solid #e2e8f0;
        }

        .notifications-header {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: white;
          border-radius: 12px 12px 0 0;
        }

        .notifications-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          display: flex;
          align-items: center;
        }

        .mark-all-read {
          padding: 6px 12px;
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 6px;
          color: #0369a1;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }

        .mark-all-read:hover {
          background: #e0f2fe;
        }

        .notifications-list {
          flex: 1;
          overflow-y: auto;
          max-height: 500px;
        }

        .notifications-loading {
          padding: 60px 20px;
          text-align: center;
          color: #64748b;
        }

        .spinner-small {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        .no-notifications {
          padding: 60px 20px;
          text-align: center;
          color: #64748b;
        }

        .no-notifications p {
          margin: 16px 0 8px;
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
        }

        .no-notifications span {
          font-size: 14px;
        }

        .notification-item {
          padding: 16px 20px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          gap: 12px;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .notification-item:hover {
          background: #f8fafc;
        }

        .notification-item.unread {
          background: #eff6ff;
        }

        .notification-item.unread:hover {
          background: #dbeafe;
        }

        .notification-icon {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: white;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-tipo {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          color: inherit;
        }

        .notification-producto {
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .notification-details {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #64748b;
          background: rgba(255, 255, 255, 0.8);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .notification-actions {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
        }

        .action-button {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .action-button:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        .action-button.delete:hover {
          background: #fef2f2;
          border-color: #fecaca;
          color: #dc2626;
        }

        .notifications-footer {
          padding: 12px 20px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: center;
          background: white;
          border-radius: 0 0 12px 12px;
        }

        .notifications-footer button {
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #64748b;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .notifications-footer button:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #1e293b;
        }

        /* ============================================
           CONTENIDO PRINCIPAL
           ============================================ */

        .main-content {
          flex: 1;
          margin-left: 280px;
          transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          min-height: 100vh;
          position: relative;
        }

        .main-content.expanded {
          margin-left: 70px;
        }

        .mobile-menu-button {
          display: none;
          position: fixed;
          top: 20px;
          left: 20px;
          z-index: 999;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          border: none;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
          transition: all 0.3s ease;
        }

        .mobile-menu-button:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 25px rgba(102, 126, 234, 0.4);
        }

        .mobile-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
        }

        /* ============================================
           RESPONSIVE
           ============================================ */

        @media (max-width: 1024px) {
          .main-sidebar {
            transform: translateX(-100%);
          }

          .main-sidebar.mobile-open {
            transform: translateX(0);
          }

          .main-content {
            margin-left: 0;
          }

          .main-content.expanded {
            margin-left: 0;
          }

          .mobile-menu-button {
            display: flex;
          }

          .mobile-overlay {
            display: block;
          }

          .close-mobile-menu {
            display: flex;
          }

          .toggle-main-sidebar-footer {
            display: flex;
          }

          .notifications-dropdown-sidebar {
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: calc(100vw - 32px);
            max-width: 400px;
            max-height: 80vh;
          }
        }

        @media (max-width: 768px) {
          .main-sidebar {
            width: 100%;
            max-width: 320px;
          }

          .mobile-menu-button {
            top: 16px;
            left: 16px;
            width: 40px;
            height: 40px;
          }

          .notifications-dropdown-sidebar {
            width: calc(100vw - 32px);
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  )
}

export default App
